import { AfterViewInit, Component, DestroyRef, ElementRef, OnInit, QueryList, ViewChildren } from '@angular/core';
import { UserService } from '../../services/user.service';
import { API, ClientDTO, Order, Pagination, shared_Client_retrieveAccessibleClients_order } from '../../services/api/api';
import { BehaviorSubject } from 'rxjs';
import { APIService } from '../../services/api/api.service';
import { NavigationService } from '@ngx-seatback/utils';
import { RoutingID } from '../../routes/sb-routing-enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';
import { ErrorService } from '../../services/error.service';

@Component({
  selector: 'sb-comp-client-selection',
  templateUrl: './client-selection.component.html',
  styleUrls: ['./client-selection.component.scss'],
  standalone: false,
})
export class ClientSelectionComponent implements OnInit, AfterViewInit {
  @ViewChildren('clientDiv') clientDivs: QueryList<ElementRef>;
  isClientSelected: boolean;

  clientsLoading: boolean;
  clientPagination: BehaviorSubject<Pagination>;
  debounceTimer: ReturnType<typeof setTimeout> = null;
  clients: ClientDTO[];

  constructor(
    public userService: UserService,
    private api: API,
    private apiService: APIService,
    private navService: NavigationService,
    private errorHandler: ErrorService,
    private destroyRef: DestroyRef
  ) {
    this.isClientSelected = false;
    this.clientsLoading = true;
    this.clientPagination = new BehaviorSubject<Pagination>({
      page: 1,
      count: 4,
      pages: -1,
      resultcount: -1,
    });
  }

  ngOnInit(): void {
    this.clientRequestDebounced();
  }

  ngAfterViewInit(): void {
    if (this.clients.length <= 1) {
      return;
    }
    if (this.isClientSelected || this.clientDivs.length !== this.clients.length) {
      return;
    }
    for (const [index, client] of this.clients.entries()) {
      const div = this.clientDivs.get(index);
      if (
        this.userService.user.user.primaryClient &&
        client.id === this.userService.user.user.primaryClient.id &&
        div &&
        div.nativeElement.id === client.id.toString()
      ) {
        div.nativeElement.focus();
        break;
      }
    }
  }

  clientRequestDebounced() {
    this.clientsLoading = true;
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.clientRequest();
    }, 500);
  }

  clientRequest() {
    this.api
      .shared_Client_retrieveAccessibleClients(
        {
          order: {
            by: shared_Client_retrieveAccessibleClients_order.CLIENT_ORDER_NUM,
            direction: Order.desc,
          },
        },
        this.clientPagination
      )
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (clientList: ClientDTO[]) => {
          this.clients = [];
          if (this.userService.user.user.primaryClient && this.clientPagination.value.page === 1) {
            this.clients.push(this.userService.user.user.primaryClient);
          }
          for (const client of clientList) {
            if (client.id !== this.userService.user.user.primaryClient?.id) {
              this.clients.push(client);
            }
          }
          if (this.clients.length === 1) {
            this.selectClient(this.clients[0]);
          }
          this.clientsLoading = false;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  selectClient(client: ClientDTO) {
    this.isClientSelected = true;
    this.userService.user.client = client;
    this.apiService.destroySubs();
    this.api
      .shared_Client_switchTo(client)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: () => {
          this.apiService.destroySubs();
          this.navService.navigateToPoint(RoutingID.home);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  loadClient() {
    this.clientPagination.value.page =
      this.clientPagination.value.page + 1 > this.clientPagination.value.pages ? 1 : this.clientPagination.value.page + 1;
    this.clientRequestDebounced();
  }

  signOut() {
    this.apiService.logout();
  }
}
